import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Tooltip } from '@shared/components/Tooltip';
import type { ColumnCustomizationPopupProps } from './types';

/** Tick mark SVG matching FilterPopup checkbox (white check on dark background). */
function CheckboxTick() {
  return (
    <svg width="12" height="9" viewBox="0 0 12 9" fill="none">
      <path
        d="M1 4.5L4 7.5L11 1"
        stroke="#FFFFFF"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const POPUP_SHADOW =
  '0px 2px 3px 0px rgba(0, 0, 0, 0.15), 0px 6px 10px 4px rgba(0, 0, 0, 0.08), 0px -2px 4px 0px rgba(0, 0, 0, 0.15)';

const DEFAULT_TITLE = 'Wybierz kolumny do wyświetlenia';
const DEFAULT_DISABLED_TOOLTIP = 'Ta kolumna jest wymagana';

export function ColumnCustomizationPopup({
  isOpen,
  onClose,
  columnOptions,
  visibleKeys,
  onVisibilityChange,
  title = DEFAULT_TITLE,
  disabledTooltipText = DEFAULT_DISABLED_TOOLTIP,
}: ColumnCustomizationPopupProps) {
  const popupRef = useRef<HTMLDivElement | null>(null);
  const [maxHeight, setMaxHeight] = useState<number | null>(null);

  const recalcMaxHeight = useCallback(() => {
    if (!popupRef.current) {
      return;
    }
    const rect = popupRef.current.getBoundingClientRect();
    const available = Math.max(160, window.innerHeight - rect.top - 16);
    setMaxHeight(available);
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }
    recalcMaxHeight();
  }, [isOpen, recalcMaxHeight]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', recalcMaxHeight);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', recalcMaxHeight);
    };
  }, [isOpen, onClose, recalcMaxHeight]);

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} aria-hidden />
      <div
        ref={popupRef}
        role="dialog"
        aria-label={title}
        className="absolute right-0 top-full z-50 mt-1 w-[280px] bg-[#FAFAFA] rounded-xl flex flex-col"
        style={{
          padding: '20px',
          boxShadow: POPUP_SHADOW,
          maxHeight: maxHeight ?? undefined,
          overflow: 'hidden',
        }}
        data-testid="column-customization-popup"
      >
        <div className="text-[#181A1B] text-sm font-medium leading-5 tracking-[0.1px] mb-0.5 shrink-0">
          {title}
        </div>
        <div className="flex flex-col gap-0.5 flex-1 min-h-0 overflow-y-auto">
          {columnOptions.map((opt) => {
            const checked = visibleKeys[opt.key] !== false;
            const row = (
              <label
                key={opt.key}
                data-testid={`column-option-${opt.key}`}
                tabIndex={opt.disabled ? 0 : undefined}
                className={`min-h-10 flex items-center gap-1 w-full cursor-pointer rounded-lg outline-none ${
                  opt.disabled
                    ? 'opacity-60 cursor-not-allowed focus-visible:ring-2 focus-visible:ring-[#2A4455] focus-visible:ring-offset-2 focus-visible:bg-[#181A1B]/4'
                    : 'has-focus-visible:ring-2 has-focus-visible:ring-[#2A4455] has-focus-visible:ring-offset-2 has-focus-visible:bg-[#181A1B]/4'
                }`}
              >
                <div className="relative flex items-center justify-center w-10 h-10 rounded-full hover:bg-[#181A1B]/8 cursor-pointer shrink-0">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() =>
                      !opt.disabled && onVisibilityChange(opt.key, !checked)
                    }
                    disabled={opt.disabled === true}
                    className="sr-only"
                    aria-label={opt.label}
                  />
                  <div
                    className={`w-[18px] h-[18px] rounded-[2px] border-2 flex items-center justify-center cursor-pointer ${
                      checked
                        ? 'bg-[#2A4455] border-[#2A4455]'
                        : 'bg-white border-[#474A4C]'
                    }`}
                  >
                    {checked && <CheckboxTick />}
                  </div>
                </div>
                <span className="text-[#181A1B] text-sm leading-5 tracking-[0.25px] cursor-pointer whitespace-nowrap">
                  {opt.label}
                </span>
              </label>
            );
            return opt.disabled ? (
              <Tooltip
                key={opt.key}
                content={disabledTooltipText}
                dark
                side="left"
                align="center"
              >
                {row}
              </Tooltip>
            ) : (
              row
            );
          })}
        </div>
      </div>
    </>
  );
}
